import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
// import { MENU_BOOK_LIST } from './mockdata';
import { CommonResponse } from 'models';
import { EatingWaySettingListItem } from './models';
import { MultiSelectToArg } from './utils/dragutils/types';
import { multiSelectTo } from './utils/dragutils/utils';

export interface EatingWaySettingState {
  loading: boolean;
  data: EatingWaySettingListItem[];
  selectedItemIds: string[];
  selectedDroppableId?: string;
  draggingItemId?: string;
}

const initialState: EatingWaySettingState = {
  loading: false,
  data: [],
  selectedItemIds: [],
};

const eatingWaySettingSlice = createSlice({
  name: 'eating_way_setting',
  initialState,
  reducers: {
    fetchEatingWaySettingList(state, action: PayloadAction<{ eatingWayId: number }>) {
      state.loading = true;
    },
    fetchEatingWaySettingListSuccess(state, action: PayloadAction<CommonResponse<EatingWaySettingListItem[]>>) {
      state.data = action.payload.data;
      state.loading = false;
    },
    fetchEatingWaySettingListFailed(state) {
      state.loading = false;
    },
    unselectAll(state) {
      state.selectedItemIds = [];
      state.draggingItemId = undefined;
      state.selectedDroppableId = undefined;
    },
    setDraggingItemId(state, action: PayloadAction<string | undefined>) {
      state.draggingItemId = action.payload;
    },
    updateState(state, action: PayloadAction<Partial<EatingWaySettingState>>) {
      state.data = action.payload.data || [];
      state.selectedItemIds = action.payload.selectedItemIds || [];
      state.draggingItemId = action.payload.draggingItemId;
    },
    toggleSelection(state, action: PayloadAction<{ selectedDroppableId: string; itemId: string }>) {
      const selectedItemIds = state.selectedItemIds;
      const wasSelected: boolean =
        state.selectedDroppableId === action.payload.selectedDroppableId &&
        selectedItemIds.includes(action.payload.itemId);

      const newItemIds: string[] = (() => {
        // Task was not previously selected
        // now will be the only selected item
        if (!wasSelected) {
          return [action.payload.itemId];
        }

        // Task was part of a selected group
        // will now become the only selected item
        if (selectedItemIds.length > 1) {
          return [action.payload.itemId];
        }

        // task was previously selected but not in a group
        // we will now clear the selection
        return [];
      })();

      state.selectedDroppableId = action.payload.selectedDroppableId;
      state.selectedItemIds = newItemIds;
    },

    toggleSelectionInGroup(state, action: PayloadAction<{ selectedDroppableId: string; itemId: string }>) {
      const selectedItemIds = state.selectedItemIds;
      const index: number = selectedItemIds.indexOf(action.payload.itemId);

      // if not selected - add it to the selected items
      if (index === -1) {
        state.selectedItemIds = [...selectedItemIds, action.payload.itemId];
        return;
      }

      // it was previously selected and now needs to be removed from the group
      const shallow = [...selectedItemIds];
      shallow.splice(index, 1);
      state.selectedItemIds = shallow;

      state.selectedDroppableId = action.payload.selectedDroppableId;
    },

    multiSelectTo(state, action: PayloadAction<{ arg: MultiSelectToArg; newItemId: string }>) {
      const updated = multiSelectTo(action.payload.arg, action.payload.newItemId);

      if (updated == null) {
        return;
      }
      state.selectedItemIds = updated;
    },
  },
});

// Actions
export const eatingWaySettingActions = eatingWaySettingSlice.actions;

// Selectors
export const selectEatingWaySettingLoading = (state: RootState) => state.eatingWaySetting.loading;
export const selectEatingWaySettingData = (state: RootState) => state.eatingWaySetting.data;
export const selectEatingWaySettingSelectedDroppableId = (state: RootState) =>
  state.eatingWaySetting.selectedDroppableId;
export const selectEatingWaySettingSelectedItemIds = (state: RootState) => state.eatingWaySetting.selectedItemIds;
export const selectEatingWaySettingDraggingItemId = (state: RootState) => state.eatingWaySetting.draggingItemId;

export const selectEatingWaySettingSelectedItemIdMap = createSelector(
  selectEatingWaySettingSelectedItemIds,
  (selectedItemIds) => {
    return selectedItemIds.reduce((previous: { [id: string]: true }, current: string): { [id: string]: true } => {
      previous[current] = true;
      return previous;
    }, {});
  }
);

// Reducer
export const eatingWaySettingReducer = eatingWaySettingSlice.reducer;
